/**
 * OTP Input Component
 *
 * Collects and verifies one-time password for phone verification.
 * LoadingBar and navigation buttons are rendered by the parent layout.
 */

"use client";

import { Button } from "@frontend/shared/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@frontend/shared/components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
} from "@frontend/shared/components/ui/input-otp";
import { cn } from "@frontend/shared/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import type { SlotProps } from "input-otp";
import { RotateCcw } from "lucide-react";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import * as React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { formatPhoneNumber, normalizePhoneInput } from "@/util/formatters";

import {
  useInsuranceConnectorContext,
  useInsuranceConnectorSend,
} from "../insurance-connector-provider";
import { INSURANCE_STEP_FORM_ID } from "../step-config";

const PillOTPSlot = React.forwardRef<
  HTMLDivElement,
  SlotProps & React.ComponentPropsWithoutRef<"div">
>(({ char, hasFakeCaret, isActive, className, ...props }, ref) => {
  const { placeholderChar, ...rest } = props;
  return (
    <div
      ref={ref}
      className={cn(
        "relative flex flex-1 h-16 items-center justify-center rounded-full bg-gray-100 text-xl font-semibold transition-all border border-transparent",
        isActive && "ring-1 ring-[#00A3FF]",
        className,
      )}
      {...rest}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="animate-caret-blink h-5 w-px bg-foreground duration-1000" />
        </div>
      )}
    </div>
  );
});
PillOTPSlot.displayName = "PillOTPSlot";

const otpSchema = z.object({
  otp: z
    .string()
    .length(6, "OTP must be 6 digits")
    .regex(/^\d+$/, "OTP must contain only numbers"),
});

type OTPFormData = z.infer<typeof otpSchema>;

export function OTPInput() {
  const t = useTranslations("InsuranceConnector");
  const context = useInsuranceConnectorContext();
  const send = useInsuranceConnectorSend();
  const [resendLoading, setResendLoading] = useState(false);

  const form = useForm<OTPFormData>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      otp: "",
    },
  });

  const onSubmit = (data: OTPFormData) => {
    send({ type: "SUBMIT", data });
  };

  const handleResend = async () => {
    setResendLoading(true);
    send({ type: "RESEND" });
    setTimeout(() => setResendLoading(false), 2000);
  };

  const otpError = context.error as
    | { code?: string; message?: string }
    | undefined;
  const hasInvalidOTPError = otpError?.code === "OTP_INVALID";

  return (
    <Form {...form}>
      <form
        id={INSURANCE_STEP_FORM_ID}
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
      >
        <h2 className="text-2xl md:text-[32px] leading-[120%] font-semibold">
          {t("EnterVerificationCode", {
            phoneNumber: formatPhoneNumber(
              normalizePhoneInput(context.phoneNumber),
            ),
          })}
        </h2>

        <FormField
          control={form.control}
          name="otp"
          render={({ field }) => (
            <FormItem className="w-full flex flex-col items-start">
              <FormLabel className="sr-only">{t("VerificationCode")}</FormLabel>
              <FormControl>
                <InputOTP
                  maxLength={6}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    if (value.length === 6) {
                      form.handleSubmit(onSubmit)();
                    }
                  }}
                  autoFocus
                  containerClassName="w-full"
                  render={({ slots }) => (
                    <InputOTPGroup className="flex w-full gap-4">
                      {slots.map((slot, idx) => (
                        <PillOTPSlot
                          // biome-ignore lint/suspicious/noArrayIndexKey: OTP slots are fixed-count and position-based
                          key={idx}
                          {...slot}
                        />
                      ))}
                    </InputOTPGroup>
                  )}
                />
              </FormControl>
              <FormMessage />
              {hasInvalidOTPError && (
                <p className="text-sm text-red-500 text-center mt-2">
                  {otpError.message}
                </p>
              )}
            </FormItem>
          )}
        />

        <div className="flex">
          <Button
            type="button"
            variant="ghost"
            onClick={handleResend}
            disabled={resendLoading}
            className="gap-2 text-lg"
          >
            <RotateCcw
              className={`h-5 w-5 ${resendLoading ? "animate-spin" : ""}`}
            />
            {resendLoading ? t("Sending") : t("ResendTheCode")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
